import { estimatesApi } from "@/lib/api/estimates"
import type { CapturedShot, ShotKey } from "./types"

// Direct submit pipeline for Quick Estimate 2. Mirrors what the v1
// EstimateProvider.saveEstimate() does, but isolated — v2 doesn't
// share state with v1's context, so we duplicate the 3-call chain
// here. The BE endpoints are stable contracts.
//
// Returns { estimateId, jobId } so the caller can drive the agent
// polling UI on the success screen.

export interface ConsumerIntakeEstimateSubmitArgs {
  teamId: number | string
  vin: string
  vehicle: {
    year: string | null
    make: string | null
    model: string | null
    trim: string | null
  } | null
  shots: Partial<Record<ShotKey, CapturedShot>>
  closeUps: CapturedShot[]
  notes: string
  customer: {
    name: string
    phone: string
  }
}

export interface ConsumerIntakeEstimateSubmitResult {
  estimateId: number
  jobId: string | null
}

export async function submitConsumerIntakeEstimate(
  args: ConsumerIntakeEstimateSubmitArgs,
): Promise<ConsumerIntakeEstimateSubmitResult> {
  // 1. Create the estimate shell (customer + vehicle).
  const estimate = await estimatesApi.startFull(args.teamId, {
    customer: {
      // BE requires a name even when the user skipped customer info —
      // fall back to "—" so we never block the create call. The
      // body shop sees "—" and knows the writer didn't capture it.
      name: args.customer.name.trim() || "—",
      ...(args.customer.phone.trim() && { phone: args.customer.phone.trim() }),
    },
    vehicle: {
      vin: args.vin,
      ...(args.vehicle?.year && { year: args.vehicle.year }),
      ...(args.vehicle?.make && { make: args.vehicle.make }),
      ...(args.vehicle?.model && { model: args.vehicle.model }),
      ...(args.vehicle?.trim && { trim: args.vehicle.trim }),
    },
  })

  // 2. Attach images. We send the agent-uploaded CDN URLs (already
  // available on each CapturedShot), not raw blobs — agent already
  // uploaded during evaluateImage. Soft-fail this step: a missing
  // image is recoverable; a missing estimate isn't.
  const allShots = [
    ...(Object.values(args.shots).filter(Boolean) as CapturedShot[]),
    ...args.closeUps,
  ]
  // Skip shots whose evaluate-image call failed (uploadedUrl is empty)
  // so we don't post an invalid image. The user has already accepted
  // these as "good enough"; we just can't send them to the BE.
  const submittable = allShots.filter((s) => s.uploadedUrl.length > 0)
  if (submittable.length > 0) {
    const images = submittable.map((s, idx) => ({
      imageUrl: s.uploadedUrl,
      side: s.detectedSide,
      qualityScore: s.qualityScore,
      qualified: s.qualified,
      description: args.notes && idx === 0 ? args.notes : "",
    }))
    try {
      await estimatesApi.addImages(estimate.id, { images })
    } catch (err) {
      // Don't fail the whole submit — surface a partial success
      // upstream. A retry in the UI is fine; the estimate exists.
      console.warn("[consumer-intake-estimate] addImages failed:", err)
    }
  }

  // 3. Kick off damage detection (returns jobId for polling). The
  // quickEstimateMode flag tells the BE to chain damage → repair
  // → cost without intermediate user steps.
  let jobId: string | null = null
  try {
    const job = await estimatesApi.runDamageEstimation(estimate.id, {
      quickEstimateMode: true,
    })
    jobId = job.jobId ?? null
  } catch (err) {
    console.warn("[consumer-intake-estimate] damage agent failed:", err)
  }

  return {
    estimateId: Number(estimate.id),
    jobId,
  }
}
